import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as SettingsActions from '../actions/settings.actions';
import * as AuthActions from '../../auth/actions/auth.actions';
import {catchError, exhaustMap, switchMap, tap} from 'rxjs/operators';
import { StorageService } from '../../../core/storage/storage.service';
import * as moment from 'moment-timezone';
import { SettingsService } from '../../../core/services/settings.service';
import { forkJoin, of } from 'rxjs';
import { AccountSettingsService } from '../../../settings/services/account-settings.service';
import { TagEnum } from '../../../settings/models/tag.enum';
import { SettingsModel } from '../../../core/models/settings.model';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { LoadingBarState } from '@ngx-loading-bar/core/loading-bar.state';

@Injectable()
export class SettingsEffects {

  setTimeZone$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.setTimeZone.type),
      tap((action: any) => {
        this._storageService.set('timeZone', action.timeZone);
        moment.tz.setDefault(action.timeZone);
      })
    ), { dispatch: false }
  );

  loadDefaultTimeZone$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.loadDefaultTimeZone.type),
      tap((action: any) => {
        const timeZone = this._storageService.get('timeZone') || moment.tz.guess();
        moment.tz.setDefault(timeZone);
      })
    ), { dispatch: false }
  );


  loadTimeZone$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.getSettingsTagsSuccess.type),
      tap((action: any) => {
        if (action.data && action.data.length > 0) {
          const timeZoneSettings: SettingsModel = action.data.find(x => x.key.toUpperCase() === TagEnum.TimeZone.name.toUpperCase());
          if (timeZoneSettings) {
            const timeZone = timeZoneSettings.value || this._storageService.get('timeZone') || moment.tz.guess();
            this._storageService.set('timeZone', timeZone);
            moment.tz.setDefault(timeZone);
          }
        }
      })
    ), { dispatch: false }
  );



  getSettings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.getSettingsTags.type),
      switchMap(action => this._settingsService.getSettings()
        .pipe(
          exhaustMap(res => of(SettingsActions.getSettingsTagsSuccess({data: res}))),
          catchError(error => of(SettingsActions.getSettingsTagsFailure(error)))
        )
      )
    )
  );

  getTag$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.getTag.type),
      switchMap(action => this._settingsService.getTag(action['tagName'])
        .pipe(
          exhaustMap(res => of(SettingsActions.getTagSuccess({data: res}))),
          catchError(error => of(SettingsActions.getTagFailure(error)))
        )
      )
    )
  );

  getTags$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.getTags.type),
      switchMap((action: any) => {
        const subs = [];
        action.tagNames.forEach((tagName: string) => subs.push(this._settingsService.getTag(tagName)));

        return forkJoin(subs).pipe(
          exhaustMap((res: SettingsModel[]) => of(SettingsActions.getTagsSuccess({data: res}))),
          catchError(error => of(SettingsActions.getTagFailure(error)))
        );
      })
    )
  );

  editTag$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.editTag.type),
      tap(() => this._loader.start()),
      switchMap(action => this._settingsService.editTag(action['tagName'], action['tagValue'])
        .pipe(
          exhaustMap(res => of(SettingsActions.editTagSuccess({data: res}))),
          catchError(error => of(SettingsActions.editTagFailure(error))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  editTags$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.editTags.type),
      tap(() => this._loader.start()),
      switchMap((action: any) => {
        const subs = [];
        action.tagNames.forEach((tagName: string) => subs.push(this._settingsService.editTag(tagName, action['tagValue'])));

        return forkJoin(subs).pipe(
          exhaustMap((res: SettingsModel[]) => of(SettingsActions.editTagsSuccess({data: res}))),
          catchError(error => of(SettingsActions.editTagsFailure(error))),
          tap(() => this._loader.complete()),
        );
      })
    )
  );


  deleteTag$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.deleteTag.type),
      switchMap(action => this._settingsService.deleteTag(action['tagName'])
        .pipe(
          exhaustMap(res => of(SettingsActions.deleteTagSuccess({tagName: action['tagName']}))),
          catchError(error => of(SettingsActions.deleteTagFailure(error)))
        )
      )
    )
  );

  deleteAccount$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.deleteAccount.type),
      switchMap(action => this._accountSettingsService.deleteAccount()
        .pipe(
          exhaustMap(res => of(AuthActions.logout())),
        )
      )
    )
  );

  changeUserAccount$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SettingsActions.changeUserAccount.type),
      tap(() => this._loader.start()),
      switchMap(action => this._accountSettingsService.changeUserAccount(action['data'])
        .pipe(
          exhaustMap(res => of(SettingsActions.changeUserAccountSuccess({data: action['data']}))),
          catchError(error => of(SettingsActions.changeUserAccountFailure(error))),
          tap(() => this._loader.complete()),
        )
      )
    )
  );

  private _loader: LoadingBarState;

  constructor(
    private _actions$: Actions,
    private _storageService: StorageService,
    private _settingsService: SettingsService,
    private _accountSettingsService: AccountSettingsService,
    private _loadingBar: LoadingBarService
  ) {
    this._loader = this._loadingBar.useRef('main');
  }
}
